import {
  Wrap,
  Text,
  Box,
} from '@chakra-ui/react';
import {
  useJourneysUnplannedEventsFreight,
  useRoadAccessLostFreight,
} from '@src/modules/performance/context/reports-api-hooks';
import TornadoBarChart from '@src/modules/performance/components/charts/TornadoBarChart';
import LockYearBadge from '@src/modules/performance/components/LockYearBadge';
import { CHART_COLOURS } from '@src/modules/performance/shared/constants';
import ReportSection from './ReportSection';

const informationSource = {
  title: 'Resilience Distributions (ONF Freight)',
  content: [
    {
      title: 'Incidents vs Journeys impacted',
      description:
  <Text fontSize="inherit" mb={2}>
    This measure shows the number of unplanned road closures with a detour provided, and the number of vehicles affected annually by those closures, summarised by ONF Freight classes.
  </Text>,
      source:
  <Text fontSize="inherit" as="span">
    Sourced from the information entered by RCAs into their Road Closures and Restrictions facility in AWM.
  </Text>,
    },
    {
      title: 'Road Closures vs Vehicles Affected',
      description:
  <Text fontSize="inherit" mb={2}>
    This measure shows the number of unplanned road closures with no detour and the number of vehicles affected by those closures annually, summarised by ONF Freight classes.
  </Text>,
      source:
  <Text fontSize="inherit" as="span">
    Sourced from the information entered by RCAs into their Road Closures and Restrictions facility in AWM.
  </Text>,
    },
  ],
};

function NetworkAvailabilityResilienceDistributionsFreight() {
  const { data: journeysUnplannedEventsOnf, isLoading: isLoadingJourneysOnf } = useJourneysUnplannedEventsFreight('onf');
  const { data: roadAccessLostOnf, isLoading: isLoadingRoadAccessOnf } = useRoadAccessLostFreight('onf');
  const isLoading = isLoadingJourneysOnf || isLoadingRoadAccessOnf;

  // Check if all lockYears are the same
  const allLockYears = [
    journeysUnplannedEventsOnf?.[0]?.LockYear,
    roadAccessLostOnf?.[0]?.LockYear,
  ];
  const primaryLockYear = allLockYears.every((year) => year === allLockYears[0]) ? allLockYears[0] : null;

  // ONF Journeys Unplanned ONF Chart
  const journeysUnplannedEventsOnfData = [
    {
      name: 'Incidents',
      type: 'bar',
      data: journeysUnplannedEventsOnf?.map((item) => ({
        x: item.ONRC,
        y: item.Num_of_Incidents ? item.Num_of_Incidents * -1 : 0,
      })),
      color: CHART_COLOURS.blue,
    },
    {
      name: 'Journeys Impacted',
      type: 'bar',
      data: journeysUnplannedEventsOnf?.map((item) => ({
        x: item.ONRC,
        y: item.Num_of_Journeys_Impacted ? item.Num_of_Journeys_Impacted : 0,
      })),
      color: CHART_COLOURS.orange,
    },
  ];

  // ONF Road Access Lost ONF Chart
  const roadAccessLostOnfData = [
    {
      name: 'Road Closures',
      type: 'bar',
      data: roadAccessLostOnf?.map((item) => ({
        x: item.ONRC,
        y: item.Number_of_Road_Closures ? item.Number_of_Road_Closures * -1 : 0,
      })),
      color: CHART_COLOURS.blue,
    },
    {
      name: 'Vehicles Affected Annually',
      type: 'bar',
      data: roadAccessLostOnf?.map((item) => ({
        x: item.ONRC,
        y: item.Number_of_Vehicles_Affected_Annually ? item.Number_of_Vehicles_Affected_Annually : 0,
      })),
      color: CHART_COLOURS.orange,
    },
  ];

  return (
    <ReportSection
      title="Resilience Distributions (ONF Freight)"
      isLoading={isLoading || !journeysUnplannedEventsOnf || !roadAccessLostOnf}
      informationSource={informationSource}
      lockYear={primaryLockYear || null}
    >
      <Wrap>
        <Box>
          {!primaryLockYear
          && (
          <LockYearBadge
            lockYear={journeysUnplannedEventsOnf?.[0]?.LockYear}
            justify="flex-start"
            mb={2}
          />
          )}
          <TornadoBarChart
            title="Incidents vs Journeys impacted"
            categories={[...new Set(journeysUnplannedEventsOnf?.map((item) => item.ONRC))]}
            series={journeysUnplannedEventsOnfData}
            paddingRight={50}
            paddingLeft={30}
            width={270}
          />
        </Box>
        <Box>
          {!primaryLockYear
          && (
          <LockYearBadge
            lockYear={roadAccessLostOnf?.[0]?.LockYear}
            justify="flex-start"
            mb={2}
          />
          )}
          <TornadoBarChart
            title="Road closures vs Vehicles affected"
            categories={[...new Set(roadAccessLostOnf?.map((item) => item.ONRC))]}
            series={roadAccessLostOnfData}
            paddingRight={50}
            paddingLeft={30}
            width={280}
          />
        </Box>
      </Wrap>
    </ReportSection>
  );
}

export default NetworkAvailabilityResilienceDistributionsFreight;
